import { useMemo } from "react";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { CATEGORY_COLORS } from "@/lib/constants";
import { getThumbnail } from "@/lib/video";

/**
 * Cmd/Ctrl+K quick-add modal. Groups techniques by category so power users
 * can find and drop any technique without opening the sidebar. Enter selects
 * the highlighted item and closes the dialog.
 */
export function TechniqueCommandPalette({ open, onOpenChange, techniques, onSelect }) {
  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of techniques) {
      const key = t.category || "Uncategorized";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [techniques]);

  const handleSelect = (t) => {
    onSelect?.(t);
    onOpenChange(false);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search techniques by name, category, or position…"
        data-testid="command-palette-input"
      />
      <CommandList data-testid="command-palette-list" className="max-h-[420px]">
        <CommandEmpty>No techniques found.</CommandEmpty>
        {grouped.map(([category, items]) => (
          <CommandGroup key={category} heading={category}>
            {items.map((t) => (
              <CommandItem
                key={t.id}
                value={`${t.name} ${t.category || ""} ${t.position || ""}`}
                onSelect={() => handleSelect(t)}
                data-testid={`command-item-${t.id}`}
                className="gap-3"
              >
                <div className="relative h-9 w-12 rounded bg-black overflow-hidden shrink-0">
                  <img src={getThumbnail(t)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium leading-tight truncate">{t.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {t.category && (
                      <span className="text-[10px] uppercase tracking-wider" style={{ color: CATEGORY_COLORS[t.category] }}>
                        {t.category}
                      </span>
                    )}
                    {t.position && <span className="text-[10px] text-muted-foreground">{t.position}</span>}
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
